import { RedoOutlined } from '@ant-design/icons';
import { Button, Select } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioGroup } from '@headlessui/react';
import classNames from 'classnames';
import OverView from './components/Overview';
import ChartConsent from './components/Chart';
import LogTable from './components/LogTable';
import ConsentService from '../../services/Consent.service';
import init from './init.json';
import moment from 'moment';
import { DateTime } from 'luxon';

const { Option } = Select;
const currentYear = new Date().getFullYear();

const select = (t) => [
    {
        name: t('Last 7 days'),
        value: 'Last 7 Days',
    },
    {
        name: t('Last 30 Days'),
        value: 'Last 30 Days',
    },
    {
        name: t('Yearly'),
        value: 'Yearly',
    },
];

const ConsentDashboard = () => {
    const { i18n } = useTranslation();
    const lang = i18n.language;
    const [isLoading, setIsLoading] = useState(false);
    const [consent, setConsent] = useState(init);
    const [refresh, setRefresh] = useState(false);
    const [yearly, setYearly] = useState(currentYear);

    const [graphResponses, setGraphResponses] = useState({
        dateLabel: '',
        graph: [],
    });
    const [dataCurrent, setDataCurrent] = useState(
        moment(new Date()).format('DD/MM/YYYY HH:mm')
    );
    const { t } = useTranslation();
    const border = (i, checked) => {
        if (checked) {
            return {};
        } else {
            if (i === 1)
                return {
                    borderTop: '1px solid #E9EBF1',
                    borderBottom: '1px solid #E9EBF1',
                };
            else
                return {
                    border: '1px solid #E9EBF1',
                };
        }
    };

    const getYeasrList = () => {
        let listYears = [];
        for (let i = currentYear - 5; i < currentYear + 1; i++) {
            listYears.push(i);
        }
        return listYears.reverse();
    };

    const [selected, setSelected] = useState(select(t)[2].value);
    const [periodObj, setPeriodObj] = useState({ period: select(t)[2].value, year: currentYear });

    // { period: selected, year: yearly }
    const getConsentGraph = async (period, year) => {
        const payload = { period, year };
        setIsLoading(true);
        try {
            const { data } = await ConsentService.getConsentGraph(payload);
            if (data.resultData) {
                const graphResponses = data.resultData.consentGraphResponses;
                setConsent(data.resultData);
                await setPeriodObj({period: period, year: year})
                const responses = {
                    dateLabel: getDateLabel(period, year, graphResponses),
                    graph: getGraphData(period, graphResponses, lang),
                };

                setGraphResponses(responses);
                setIsLoading(false);
            }
        } catch (error) {
            console.log(error);
            setIsLoading(false);
        }
    };

    const getDateLabel = (period, year, graphResponses) => {
        if (period === 'Last 7 Days' || period === 'Last 30 Days') {
            return `${DateTime.fromISO(graphResponses[0].date).toFormat(
                'dd/MM/yyyy'
            )} - ${DateTime.fromISO(
                graphResponses[graphResponses.length - 1].date
            ).toFormat('dd/MM/yyyy')}`;
        } else {
            return year;
        }
    };

    const getGraphData = (period, graphResponses, lang) => {
        const accepted = [];
        const rejected = [];

        graphResponses.forEach((graph) => {
            const date = formatDate(period, graph.date, lang);
            accepted.push({
                date,
                value: graph.acceptedValue,
                type: 'Accepted',
            });
            rejected.push({
                date,
                value: graph.rejectedValue,
                type: 'Rejected',
            });
        });

        return accepted.concat(rejected);
    };

    const formatDate = (period, date, lang) => {
        if (period === 'Yearly') {
            return lang === 'en'
                ? DateTime.fromISO(date).toFormat('MMMM')
                : DateTime.fromISO(date, {
                      locale: 'th',
                  }).toFormat('MMMM');
        } else {
            return lang === 'en'
                ? DateTime.fromISO(date).toFormat('dd MMM yyyy')
                : DateTime.fromISO(date, { locale: 'th' }).toFormat(
                      'dd MMM yyyy'
                  );
        }
    };

    const onChangePeriod = (value) => {
        setSelected(value);
        getConsentGraph(value, currentYear);
        setYearly(currentYear)
      
    };

    useEffect(() => {
        getConsentGraph(selected, yearly);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [yearly, lang]);

    return (
        <div>
            <div className="title ">{t('Consent Dashboard')}</div>
            <div className="sub-title">
                <span className="text-sm text-gray-400">
                    {t('Consent Management')} /{' '}
                </span>
                <span> {t('Consent Dashboard')}</span>
            </div>
            <div className="text-right flex items-center space-x-2 justify-end">
                <div className="text-sm text-[#65707A]">
                    {t('Last Update')} : {dataCurrent}
                </div>
                <Button
                    icon={<RedoOutlined />}
                    type="primary"
                    size="large"
                    className="bg-[#65707A] text-base border-none"
                    onClick={() => {
                        getConsentGraph(selected, yearly);
                        setSelected(select(t)[2].value);
                        setRefresh(true);
                        setDataCurrent(
                            moment(new Date()).format('DD/MM/YYYY HH:mm')
                        );
                    }}
                >
                    {t('Refresh')}
                </Button>
            </div>
            <div className="bg-white my-6 rounded-lg py-4 px-3 flex justify-between items-center">
                <span className="text-xl font-medium text-purple ">
                    {t('Consent Dashboard')}
                </span>
                <div className="flex items-center space-x-2">
                    {selected === 'Yearly' && (
                        <Select
                            size="large"
                            className="text-left text-[14px]"
                            placeholder="Please Select"
                            style={{ width: 140 }}
                            defaultValue={currentYear}
                            onChange={(value) => setYearly(value)}
                        >
                            {getYeasrList()?.map((year) => (
                                <Option key={year} value={year}>
                                    {year}
                                </Option>
                            ))}
                        </Select>
                    )}
                    <RadioGroup value={selected} onChange={onChangePeriod}>
                        <div className="flex items-center">
                            {select(t).map((option, i) => (
                                <RadioGroup.Option
                                    key={option.value}
                                    value={option.value}
                                    style={border(i, selected === option.value)}
                                    className={({ checked }) =>
                                        classNames(
                                            'relative flex cursor-pointer px-3 py-2 focus:outline-none',
                                            {
                                                'bg-[#F3F5F8] text-purple':
                                                    checked,
                                                'rounded-tl-lg rounded-bl-lg':
                                                    i === 0,
                                                'rounded-tr-lg rounded-br-lg':
                                                    i === 2,
                                            }
                                        )
                                    }
                                >
                                    <div className="flex w-full items-center justify-between">
                                        <div className="flex items-center">
                                            <div className="text-sm">
                                                <RadioGroup.Label as="span">
                                                    {option.name}
                                                </RadioGroup.Label>
                                            </div>
                                        </div>
                                    </div>
                                </RadioGroup.Option>
                            ))}
                        </div>
                    </RadioGroup>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
                <OverView consent={consent} isLoading={isLoading} />
                <ChartConsent
                    graphResponses={graphResponses}
                    isLoading={isLoading}
                />
                <LogTable
                    refresh={refresh}
                    setRefresh={setRefresh}
                    periodObj={periodObj}
                    selected={selected}
                />
            </div>
        </div>
    );
};

export default ConsentDashboard;
